import {ProductWidget} from '@/lib/TypesOpenApi';
import {DeepReadonly} from '@/lib/typings/readonly';
import deepEqual from 'deep-equal';

/**
 * Selected products can be updated on back-end during /buyflow execution.
 * We need compare to understand what was added, what removed and what changed.
 * @returns Object with 4 respective arrays
 * */
export const compareSelectedProducts = (
  oldSelections: Array<ProductWidget>,
  newSelections: Array<ProductWidget>,
): ArrayComparisionResults<ProductWidget> =>
  deepCompareArrays(oldSelections, newSelections, isProductsEqual);

export const isProductsEqual = (
  a: DeepReadonly<ProductWidget>,
  b: DeepReadonly<ProductWidget>,
) => {
  if (a.productId === b.productId) {
    if (a.productType !== 'CPE_POINT_PRODUCT') {
      return true;
    } else if (a.productCost?.unitText === b.productCost?.unitText) {
      return true;
    }
    return false;
  }
  return false;
};

export interface ArrayComparisionResults<T> {
  added: Array<T>;
  removed: Array<T>;
  changed: Array<T>;
  rest: Array<T>; // this stayed the same as they were
}

export interface IdComparisionHandler<T> {
  (a: T, b: T): boolean;
}

/** Abstract implementation of selected products comparision for easier testing */
export const deepCompareArrays = <T extends object = object>(
  oldItems: Array<T>,
  newItems: Array<T>,
  compareIds: IdComparisionHandler<T>,
): ArrayComparisionResults<T> => {
  const {same, restFromA, restFromB} = compareArrays(
    oldItems,
    newItems,
    compareIds,
  );

  const changed: Array<T> = [];
  const rest: Array<T> = [];

  // will iterate over same array to find what was changed or not
  for (const {a: old, b: newItem} of same) {
    // swalow compare objects for change, less performant than JSON comparision,
    // but we can expect that order of fields will change in response
    // More info https://stackoverflow.com/a/1144249
    if (deepEqual(old, newItem)) {
      rest.push(old);
    } else {
      changed.push(newItem);
    }
  }

  return {
    changed,
    // The left items in old array will be consided as removed
    removed: restFromA,
    // The left items on new array is consided as new items
    added: restFromB,
    rest,
  };
};

export interface Pair<T> {
  a: T;
  b: T;
}

export interface SplitResult<T> {
  same: Array<Pair<T>>;
  restFromA: Array<T>;
  restFromB: Array<T>;
}

/* Check which elements present in both array, and returns them as same */
export const compareArrays = <T>(
  a: Array<T>,
  b: Array<T>,
  compareIds: IdComparisionHandler<T>,
): SplitResult<T> => {
  const same: Array<Pair<T>> = [];
  const restFromA: Array<T> = [];

  // will remove all found items from "rest from B" array. Everything that left is consided as rest items
  const restFromB: Array<T> = b.slice(0);

  for (const item of a) {
    const foundIndex = restFromB.findIndex(bItem => compareIds(item, bItem));
    if (foundIndex === -1) {
      // item not found in B, will mark it as "rest from A"
      restFromA.push(item);
      continue;
    }

    // item present in both arrays, will remove from B and add to "same"
    same.push({
      a: item,
      b: restFromB[foundIndex],
    });

    // remove found element
    restFromB.splice(foundIndex, 1);
  }

  return {same, restFromA, restFromB};
};

export interface FilterResult<T> {
  result: Array<T>;
  rest: Array<T>;
}

/** Split array by given criterial predicate, resturns result array for which criteria is true and rest as separate array */
export const filterWithRest = <T>(
  arr: DeepReadonly<Array<T>> | Array<T>,
  predicate: (value: T, index: number, array: T[]) => boolean,
): FilterResult<T> => {
  const rest: Array<T> = [];

  const result = arr.filter((value, i, all) => {
    const isCorrect = predicate(value as T, i, all as Array<T>);
    if (!isCorrect) {
      rest.push(value as T);
    }

    return isCorrect;
  }) as Array<T>;

  return {
    result,
    rest,
  };
};
